import { useState, useEffect } from 'react'
import { useAuth } from './use-auth'
import { useAdmin } from './use-admin'

interface PremiumStatus {
  isPremium: boolean
  source?: 'subscription' | 'override' | 'admin' | null
  productIds?: string[]
  loading: boolean
  error?: string
}

const EDGE_FUNCTION_URL = import.meta.env.VITE_EDGE_FUNCTION_URL || 
  'https://xuqkgeusasosxtcfhbwj.supabase.co/functions/v1'

/**
 * Hook to check if current user has premium access
 * Admin users always have premium access
 * Automatically checks on mount and when user changes
 */
export function usePremium() {
  const { user } = useAuth()
  const { isAdmin, loading: adminLoading } = useAdmin()
  const [status, setStatus] = useState<PremiumStatus>({
    isPremium: false,
    loading: true,
    source: null
  })

  useEffect(() => {
    if (!user) {
      setStatus({ isPremium: false, loading: false, source: null })
      return
    }

    // Wait for admin check to complete
    if (adminLoading) {
      setStatus(prev => ({ ...prev, loading: true }))
      return
    }

    checkPremium()
  }, [user, isAdmin, adminLoading])

  async function checkPremium() {
    // Admin users always have premium access
    if (isAdmin) {
      setStatus({ isPremium: true, loading: false, source: 'admin' })
      return
    }

    setStatus(prev => ({ ...prev, loading: true, error: undefined }))

    try {
      const { data: { session } } = await supabase.auth.getSession()
      
      if (!session?.access_token) {
        throw new Error('No access token')
      }

      const response = await fetch(
        `${EDGE_FUNCTION_URL}/is-premium`,
        {
          headers: {
            'Authorization': `Bearer ${session.access_token}`
          }
        }
      )

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`)
      }

      const data = await response.json()

      setStatus({
        isPremium: data.isPremium,
        source: data.source,
        productIds: data.productIds,
        loading: false
      })
    } catch (error: any) {
      console.error('Error checking premium status:', error)
      setStatus({
        isPremium: false,
        loading: false,
        error: error.message
      })
    }
  }

  return {
    isPremium: status.isPremium,
    source: status.source,
    productIds: status.productIds,
    loading: status.loading,
    error: status.error,
    refresh: checkPremium
  }
}

/**
 * Hook to check premium status for a specific user (admin only)
 */
export function useUserPremium(userId?: string) {
  const { user } = useAuth()
  const [status, setStatus] = useState<PremiumStatus>({
    isPremium: false,
    loading: true
  })

  useEffect(() => {
    if (!user || !userId) {
      setStatus({ isPremium: false, loading: false })
      return
    }

    checkUserPremium()
  }, [user, userId])

  async function checkUserPremium() {
    if (!user || !userId) return

    setStatus(prev => ({ ...prev, loading: true, error: undefined }))

    try {
      const { data: { session } } = await supabase.auth.getSession()
      
      if (!session?.access_token) {
        throw new Error('No access token')
      }

      const response = await fetch(
        `${EDGE_FUNCTION_URL}/is-premium?userId=${userId}`,
        {
          headers: {
            'Authorization': `Bearer ${session.access_token}`
          }
        }
      )

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`)
      }

      const data = await response.json()

      setStatus({
        isPremium: data.isPremium,
        source: data.source,
        productIds: data.productIds,
        loading: false
      })
    } catch (error: any) {
      console.error('Error checking user premium status:', error)
      setStatus({
        isPremium: false,
        loading: false,
        error: error.message
      })
    }
  }

  return {
    isPremium: status.isPremium,
    source: status.source,
    productIds: status.productIds,
    loading: status.loading,
    error: status.error,
    refresh: checkUserPremium
  }
}

// Import supabase client
import { supabase } from '@/integrations/supabase/client'
